import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountUtils, cn, CurrencyUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import type { AccountResponse } from '@shared/api';
import { Button, Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import styles from './accountPaymentTile.module.scss';

interface AccountPaymentTileProps {
  className?: string;
  account: AccountResponse;
  mainAccount?: AccountResponse;
  subAccounts?: AccountResponse[];
  accounts?: boolean;
  noHover?: boolean;
  selectedAccountId?: number;
  onAccountSwitch?: (accountId: number) => void;
}

export const AccountPaymentTile = (props: AccountPaymentTileProps) => {
  const [options, setOptions] = useState<boolean>(true);

  const { t } = useTranslation('accounts');

  const accountDetails = useMemo(
    () => [
      { label: t('payment.tile.fields.iban'), value: props.account.iban },
      { label: t('payment.tile.fields.accountNumber'), value: props.account.accountNumber },
      ...(!props.accounts
        ? [{ label: t('payment.tile.fields.accountStatus'), value: t(`statuses.account.${props.account.status}`, { ns: 'common' }) }]
        : []),
    ],
    [t, props.account],
  );
  return (
    <div
      className={cn(
        'group flex h-full flex-col transition-shadow duration-200',
        !props.noHover && 'hover:shadow-lg dark:hover:shadow-lg-dark',
        !props.noHover && styles['account-payment-card'],
        props.className,
      )}
    >
      <div
        className={cn(
          'flex flex-row justify-between bg-black bg-cover bg-center bg-no-repeat px-5 py-3',
          styles['bg-account-payment'],
          PaletteUtils.isVcfcPalette && styles.vcfc,
          PaletteUtils.isPersonalPalette && '!bg-[linear-gradient(180deg,#4c171808_0%,#4C1718_100%)]',
        )}
      >
        <div className='flex flex-col'>
          <Text weight='semibold' color='white'>
            {props.account.name || FormatUtils.formatAccountNumber(props.account.accountNumber)}
          </Text>
          <Text uppercase size='xs' color='gray'>
            {props.account.productName}
          </Text>
        </div>
      </div>
      <div className={cn('flex h-full flex-col bg-white p-5 dark:bg-soft-black', !props.accounts && 'gap-3')}>
        <Text size='3xl'>{AmountUtils.transformToAmountWithCurrency(props.account.availableBalance ?? 0, props.account.currency)}</Text>
        {!props.accounts && (
          <div className='flex gap-2 max-sm:flex-col'>
            <Button
              className={cn(
                'h-fit rounded border border-light-gray px-4 py-2 text-xs font-semibold uppercase text-light-gray',
                'hover:bg-primary hover:text-white hover:opacity-100',
                props.account.mainAccId === null && props.selectedAccountId === props.account.id && 'bg-primary text-white',
              )}
              disabled={props.mainAccount === undefined}
              onClick={() => props.onAccountSwitch?.(props.account.mainAccId === null ? props.account.id : props.account.mainAccId)}
            >
              {CurrencyUtils.getSymbolByCode(props.mainAccount ? props.mainAccount.currency : '')} {t('accountType.main', { ns: 'common' })}
            </Button>
            {props.subAccounts?.map((subAccount) => (
              <Button
                key={subAccount.id}
                className={cn(
                  'h-fit rounded border border-light-gray px-4 py-2 text-xs font-semibold uppercase text-light-gray',
                  'hover:bg-primary hover:text-white hover:opacity-100',
                  props.selectedAccountId === subAccount.id && 'bg-primary text-white',
                )}
                onClick={() => props.onAccountSwitch?.(subAccount.id)}
              >
                {CurrencyUtils.getSymbolByCode(subAccount.currency)} {t('accountType.sub', { ns: 'common' })}
              </Button>
            ))}
          </div>
        )}
        <div className={!props.accounts ? 'flex gap-6 max-sm:flex-col' : 'mt-2 flex flex-col gap-2'}>
          {accountDetails.map((item, index) => (
            <div key={index}>
              <Text size='xs'>{item.label}</Text>
              <Text size='xs' weight='semibold'>
                {item.value}
              </Text>
            </div>
          ))}
        </div>
        {props.accounts && !!props.subAccounts?.length && (
          <div className='h-full'>
            <div className='flex min-h-12 cursor-pointer items-center justify-between px-0 py-4'>
              <Text size='xs'>{t('payment.tile.otherCurrencies.title')}</Text>
              <div className='flex gap-1'>
                {props.subAccounts &&
                  props.subAccounts.length > 0 &&
                  props.subAccounts.map((account) => (
                    <div key={account.id} className='flex h-5 items-center rounded border px-1.5 text-xs font-semibold text-light-gray'>
                      {CurrencyUtils.getSymbolByCode(account.currency)}
                    </div>
                  ))}
                <span
                  onClick={(e) => (e.stopPropagation(), setOptions((prev) => !prev))}
                  className={cn(
                    'mx-2 flex h-4 w-4 flex-none items-center justify-center text-center text-xl text-slate transition-transform duration-200 dark:text-gray',
                    options ? 'rotate-90' : '-rotate-90',
                  )}
                >
                  <Icon icon={<ChevronIcon />} />
                </span>
              </div>
            </div>
            <div className={cn('pt-3', !options ? 'hidden' : 'block')}>
              {props.subAccounts?.length > 0 && (
                <div>
                  {props.subAccounts.map((subAccount) => (
                    <div key={subAccount.id} className='mb-2 flex items-center gap-3 bg-ice px-3 py-2 dark:bg-slate'>
                      <div className='flex h-full items-center justify-center rounded border px-2 uppercase text-primary'>
                        <span className='ml-1 mr-2.5 text-base'>{CurrencyUtils.getSymbolByCode(subAccount.currency)}</span>
                        <Text className='mt-0.5' uppercase weight='semibold' size='xs' color='primary'>
                          {t('accountType.type.sub', { ns: 'common' })}
                        </Text>
                      </div>
                      <div>
                        <Text size='xs'>{t('payment.tile.otherCurrencies.fields.accountBalance')}</Text>
                        <Text weight='semibold' size='xs'>
                          {AmountUtils.transformToAmountWithCurrency(subAccount.availableBalance ?? 0, subAccount.currency)}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
